import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';

import { TextButton } from '../../buttons/TextButton';

import styles from './MultiSelectButton.module.css';

export interface MultiSelectButtonOption<T> {
  label: string | ReactNode;
  value: T;
  disabledOption?: boolean;
}

interface MultiSelectButtonTheme {
  MultiSelectButton?: string;
  Option?: string;
  OptionsRow?: string;
}

interface MultiSelectButtonBaseProps<T> {
  dataHcName: string;
  options: MultiSelectButtonOption<T>[];
  disabled?: boolean;
  className?: string;
  label?: ReactNode;
  primary?: boolean;
  /**
   * If set and `options.length` exceeds this number, only the first
   * `maxVisibleOptions` options are rendered with a "Show more"/"Show less" toggle
   */
  maxVisibleOptions?: number;
  theme?: MultiSelectButtonTheme;
}

export interface MultiSelectButtonPropsMulti<T>
  extends MultiSelectButtonBaseProps<T> {
  singleSelect?: false;
  value?: T[] | null;
  onChange: (value: T[]) => void;
}

export interface MultiSelectButtonPropsSingle<T>
  extends MultiSelectButtonBaseProps<T> {
  singleSelect: true;
  value?: T | null;
  onChange: (value: T | null) => void;
}

export type MultiSelectButtonProps<T> =
  | MultiSelectButtonPropsMulti<T>
  | MultiSelectButtonPropsSingle<T>;

export const MultiSelectButton = <T extends number | string | boolean | null>({
  dataHcName,
  options,
  disabled,
  className,
  label,
  primary,
  maxVisibleOptions,
  theme,
  ...restProps
}: MultiSelectButtonProps<T>) => {
  const [showAll, setShowAll] = useState(false);
  const showToggle =
    maxVisibleOptions != null && options.length > maxVisibleOptions;
  const visibleOptions =
    showToggle && !showAll ? options.slice(0, maxVisibleOptions) : options;

  const handleMultiChange = (
    v: T,
    currentValue: T[],
    onChange: (value: T[]) => void,
  ) => {
    const updated = [...currentValue];
    const idx = updated.indexOf(v);
    if (idx > -1) {
      updated.splice(idx, 1);
    } else {
      updated.push(v);
    }
    onChange(updated);
  };

  const isSelected = (optionValue: T): boolean => {
    if (restProps.singleSelect) {
      return restProps.value === optionValue;
    }
    return (restProps.value ?? []).indexOf(optionValue) > -1;
  };

  const handleClick = (optionValue: T) => {
    if (restProps.singleSelect) {
      const newValue = restProps.value === optionValue ? null : optionValue;
      restProps.onChange(newValue);
    } else {
      handleMultiChange(optionValue, restProps.value ?? [], restProps.onChange);
    }
  };

  return (
    <div className={classNames(styles.MultiSelectButton, className)}>
      {label}
      <div className={classNames(styles.OptionsRow, theme?.OptionsRow)}>
        {visibleOptions.map((option) => {
          const selected = isSelected(option.value);
          const isDisabled = disabled || option.disabledOption;
          return (
            <button
              key={`option-${option.value}`}
              data-hc-name={dataHcName}
              className={classNames(
                styles.Option,
                selected && styles.selected,
                selected && primary && styles.primary,
                isDisabled && styles.disabled,
              )}
              disabled={isDisabled}
              aria-pressed={selected}
              type="button"
              onClick={() => handleClick(option.value)}
            >
              <span
                className={styles.OptionLabel}
                title={
                  typeof option.label === 'string' ? option.label : undefined
                }
              >
                {option.label}
              </span>
            </button>
          );
        })}
        {showToggle && (
          <TextButton
            dataHcName={`${dataHcName}-show-more`}
            className={styles.ShowMoreButton}
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? 'Show fewer' : 'Show more'}
          </TextButton>
        )}
      </div>
    </div>
  );
};
